import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import Layout from '../components/Layout';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import ShareLinksModal from '../components/ShareLinksModal';
import { useSession } from '../context/SessionContext';
import io from 'socket.io-client';
import { DEFAULT_QR_TEMPLATE, normalizeQrTemplate } from '../utils/qrTemplateSettings';

// QR Template Settings Component
const QRTemplateSettings = ({ sessionId, socket }) => {
  const [qrTemplate, setQrTemplate] = useState(() => normalizeQrTemplate(DEFAULT_QR_TEMPLATE));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const activeMode = qrTemplate.display_mode === 'lower-third' ? 'lower-third' : 'fullscreen';
  const modeValues = qrTemplate.modes?.[activeMode] || DEFAULT_QR_TEMPLATE.modes[activeMode];
  const modeLabel = activeMode === 'lower-third' ? 'Lower Third' : 'Fullscreen';

  // Fetch QR template settings
  useEffect(() => {
    const fetchQRTemplate = async () => {
      try {
        const response = await api.get(`/api/qr-templates/session/${sessionId}`);
        setQrTemplate(normalizeQrTemplate(response.data));
      } catch (err) {
        console.error('Error fetching QR template:', err);
      }
    };
    
    if (sessionId) {
      fetchQRTemplate();
    }
  }, [sessionId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'display_mode') {
      setQrTemplate(prev => ({
        ...prev,
        display_mode: value === 'lower-third' ? 'lower-third' : 'fullscreen'
      }));
      return;
    }
    setQrTemplate(prev => {
      const mode = prev.display_mode === 'lower-third' ? 'lower-third' : 'fullscreen';
      return {
        ...prev,
        modes: {
          ...prev.modes,
          [mode]: {
            ...prev.modes[mode],
            [name]: value
          }
        }
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const response = await api.put(`/api/qr-templates/session/${sessionId}`, normalizeQrTemplate(qrTemplate));
      setQrTemplate(normalizeQrTemplate(response.data));
      setMessage({ type: 'success', text: 'QR template saved successfully!' });
      
      // Emit socket event to update display views
      if (socket) {
        socket.emit('qr-template-updated', {
          sessionId,
          template: response.data
        });
      }
      
      // Track event
      if (window.umami) {
        window.umami.track('QR Template Updated');
      }
    } catch (err) {
      console.error('Error saving QR template:', err);
      setMessage({ type: 'error', text: 'Failed to save QR template. Please try again.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  return (
    <div>
      <details className="group">
        <summary className="cursor-pointer text-xl font-semibold text-blue-600 hover:text-blue-800 flex items-center mb-4">
          <span>📱 QR Code Display Settings</span>
          <span className="ml-2 text-sm group-open:rotate-180 transition-transform">▼</span>
        </summary>
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-4">
            Editing {modeLabel} only. Fullscreen and Lower Third each keep their own settings. Use the "Show QR" button in Question Manager to display it.
          </p>
          
          {message && (
            <div className={`mb-4 p-3 rounded-md ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {message.text}
            </div>
          )}
          
          <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Mode
          </label>
          <select
            name="display_mode"
            value={qrTemplate.display_mode}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="fullscreen">Fullscreen - Full screen centered display</option>
            <option value="lower-third">Lower Third - Compact rounded frame</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {qrTemplate.display_mode === 'fullscreen' 
              ? 'These controls apply only to Fullscreen.' 
              : 'These controls apply only to Lower Third.'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Call to Action Text ({modeLabel})
          </label>
          <Input
            type="text"
            name="call_to_action"
            value={modeValues.call_to_action}
            onChange={handleChange}
            placeholder="Ask a question"
          />
          <p className="text-xs text-gray-500 mt-1">This text appears next to the QR code</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Horizontal Position (%)
            </label>
            <Input
              type="number"
              name="position_x"
              value={modeValues.position_x}
              onChange={handleChange}
              step="0.1"
            />
            <p className="text-xs text-gray-500 mt-1">Decimals allowed. 0 = Left, 50 = Center, 100 = Right</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vertical Position (%)
            </label>
            <Input
              type="number"
              name="position_y"
              value={modeValues.position_y}
              onChange={handleChange}
              step="0.1"
            />
            <p className="text-xs text-gray-500 mt-1">Decimals allowed. 0 = Top, 85 = Lower Third, 100 = Bottom</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Background Color
            </label>
            <div className="flex gap-2">
              <Input
                type="color"
                name="background_color"
                value={modeValues.background_color}
                onChange={handleChange}
                className="w-16 h-10"
              />
              <Input
                type="text"
                name="background_color"
                value={modeValues.background_color}
                onChange={handleChange}
                placeholder="#1e3a8a"
                className="flex-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Text Color
            </label>
            <div className="flex gap-2">
              <Input
                type="color"
                name="text_color"
                value={modeValues.text_color}
                onChange={handleChange}
                className="w-16 h-10"
              />
              <Input
                type="text"
                name="text_color"
                value={modeValues.text_color}
                onChange={handleChange}
                placeholder="#ffffff"
                className="flex-1"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            QR Code Size (pixels)
          </label>
          <Input
            type="number"
            name="qr_size"
            value={modeValues.qr_size}
            onChange={handleChange}
            min="1"
            step="any"
          />
          <p className="text-xs text-gray-500 mt-1">Any positive size is applied exactly for {modeLabel}.</p>
        </div>


        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Custom CSS (Advanced)
          </label>
          <textarea
            name="custom_css"
            value={modeValues.custom_css}
            onChange={handleChange}
            rows="6"
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
            placeholder=".qr-call-to-action { font-size: 80px; }"
          ></textarea>
          <p className="text-xs text-gray-500 mt-1">
            Override default styles. Use classes: .qr-display-container, .qr-text-section, .qr-call-to-action, .qr-code-section, .qr-lower-third-container
          </p>
          <div className="mt-2 p-3 bg-gray-50 rounded text-xs">
            <p className="font-medium mb-2">Example customizations:</p>
            <pre className="text-gray-600">{`/* Customize shadow */
.qr-lower-third-container {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

/* Customize border radius */
.qr-lower-third-container {
  border-radius: 30px;
}

/* Customize padding */
.qr-lower-third-container {
  padding: 30px 40px;
}`}</pre>
          </div>
        </div>

            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full"
            >
              {saving ? 'Saving...' : 'Save QR Template'}
            </Button>
          </div>
        </div>
      </details>
    </div>
  );
};

const TemplateEditor = () => {
  const { sessionId } = useParams();
  const { session, loading: sessionLoading } = useSession();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sessionTemplate, setSessionTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    css: '',
    html: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewQuestion, setPreviewQuestion] = useState('How does this question look in the template?');
  const [previewSubmitter, setPreviewSubmitter] = useState('John Doe');
  const [showSubmitter, setShowSubmitter] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  
  // Create a session object that will be used by Layout if the actual session isn't available
  const [localSession, setLocalSession] = useState(null);
  
  // Define fetchData as a useCallback to avoid dependency issues
  const fetchData = useCallback(async () => {
    if (!sessionId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all templates
      const templatesResponse = await api.get('/api/templates');
      setTemplates(templatesResponse.data);
      console.log('Fetched templates:', templatesResponse.data);
      
      // Fetch the template assigned to this session
      let hasTemplate = false;
      try {
        const sessionTemplateResponse = await api.get(`/api/sessions/${sessionId}/template`);
        if (sessionTemplateResponse.data) {
          setSessionTemplate(sessionTemplateResponse.data);
          console.log('Fetched session template:', sessionTemplateResponse.data);
          hasTemplate = true;
        }
      } catch (err) {
        console.log('No template assigned to this session yet');
        setSessionTemplate(null);
        hasTemplate = false;
      }
      
      // If no template is assigned and we have templates available, assign the first one
      if (!hasTemplate && templatesResponse.data && templatesResponse.data.length > 0) {
        const firstTemplate = templatesResponse.data[0];
        console.log('Auto-assigning first template:', firstTemplate.name);
        
        try {
          await api.post(`/api/sessions/${sessionId}/template`, {
            templateId: firstTemplate.id
          });
          setSessionTemplate(firstTemplate);
          console.log('Successfully assigned first template to session');
          
        } catch (err) {
          console.error('Failed to auto-assign first template:', err);
        }
      }
      
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
      setLoading(false);
    }
  }, [sessionId]);
  
  useEffect(() => {
    // If we have a session ID but no session, fetch session details and create a local session object
    const fetchSessionInfo = async () => {
      if (sessionId && !session) {
        try {
          const response = await api.get(`/api/sessions/${sessionId}`);
          const sessionName = response.data && response.data.name 
            ? response.data.name 
            : "Live Q&A Session";
            
          setLocalSession({
            id: sessionId,
            name: sessionName
          });
        } catch (err) {
          console.error('Error fetching session info:', err);
          setError('Failed to load session information');
        }
      }
    };
    
    fetchSessionInfo();
  }, [sessionId, session]);
  
  // Initialize socket connection
  useEffect(() => {
    if (sessionId) {
      // Determine the socket URL based on environment
      let socketUrl;
      
      if (process.env.NODE_ENV === 'development') {
        // In development, use the environment variable or fallback to localhost:3001
        socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';
      } else {
        // In production, use the same origin
        socketUrl = window.location.origin;
      }
      
      console.log('Connecting to socket at:', socketUrl);
      console.log('NODE_ENV:', process.env.NODE_ENV);
      
      const newSocket = io(socketUrl, {
        path: '/socket.io/',
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000,
        transports: ['websocket', 'polling'],
        withCredentials: true
      });
      
      // Store socket in ref for cleanup
      socketRef.current = newSocket;
      
      newSocket.on('connect', () => {
        console.log('Socket connected:', newSocket.id);
        
        // Join the session room
        const roomName = `session-${sessionId}`;
        console.log(`Joining room: ${roomName}`);
        newSocket.emit('join-room', roomName);
      });
      
      newSocket.on('disconnect', (reason) => {
        console.log(`Socket disconnected: ${reason}`);
        if (reason === 'io server disconnect') {
          // The disconnection was initiated by the server, try to reconnect
          newSocket.connect();
        }
      });
      
      newSocket.on('error', (error) => {
        console.error('Socket error:', error);
      });
      
      newSocket.on('reconnect', (attemptNumber) => {
        console.log(`Socket reconnected after ${attemptNumber} attempts`);
        const roomName = `session-${sessionId}`;
        console.log(`Rejoining room after reconnection: ${roomName}`);
        newSocket.emit('join-room', roomName);
      });
      
      setSocket(newSocket);
      
      // Clean up socket connection when component unmounts
      return () => {
        console.log('Cleaning up socket connection');
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
      };
    }
  }, [sessionId]);

  // Fetch data when component mounts
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update selected template when session template changes
  useEffect(() => {
    if (sessionTemplate && templates.length > 0) {
      const template = templates.find(t => t.id === sessionTemplate.id);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [sessionTemplate, templates]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleEditTemplate = () => {
    if (!selectedTemplate) return;
    
    setFormData({
      name: selectedTemplate.name,
      css: selectedTemplate.css,
      html: selectedTemplate.html,
      is_default: selectedTemplate.is_default === 1
    });
    
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleCreateTemplate = () => {
    setFormData({
      name: '',
      css: `.question-container {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  min-width: 500px;
}

.question-content {
  font-size: 24px;
  color: #333;
  margin-bottom: 10px;
}

.submitter-name {
  font-size: 16px;
  color: #666;
  font-style: italic;
}`,
      html: `<div class="question-container">
  <div class="question-content">{{content}}</div>
  {{#if submitterName}}
    <div class="submitter-name">Asked by: {{submitterName}}</div>
  {{/if}}
</div>`,
      is_default: false
    });
    
    setIsEditing(false);
    setIsCreating(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleSaveTemplate = async () => {
    try {
      // Ensure the template has min-width:500px
      let cssWithMinWidth = formData.css;
      if (!cssWithMinWidth.includes('min-width')) {
        cssWithMinWidth = cssWithMinWidth.replace(
          /\.question-container\s*\{/,
          '.question-container {\n  min-width: 500px;'
        );
      }
      
      const updatedFormData = {
        ...formData,
        css: cssWithMinWidth
      };
      
      console.log('Saving template');
      
      let savedTemplate;
      
      if (isCreating) {
        // Create new template
        const response = await api.post('/api/templates', updatedFormData);
        savedTemplate = response.data;
      } else {
        // Update existing template
        const response = await api.put(`/api/templates/${selectedTemplate.id}`, updatedFormData);
        savedTemplate = response.data;
      }
      
      // Update templates list
      const updatedTemplates = templates.map(t => 
        t.id === savedTemplate.id ? savedTemplate : t
      );
      
      if (isCreating) {
        updatedTemplates.push(savedTemplate);
      }
      
      setTemplates(updatedTemplates);
      setSelectedTemplate(savedTemplate);
      setIsEditing(false);
      setIsCreating(false);
      setError(null);
      
      // If this is the current session template, update it
      if (sessionTemplate && savedTemplate.id === sessionTemplate.id) {
        setSessionTemplate(savedTemplate);
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template. Please try again.');
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      console.log('Applying template');
      console.log('Session ID:', sessionId);
      console.log('Template ID:', selectedTemplate.id);
      
      // Assign the template to the session
      await api.post(`/api/sessions/${sessionId}/template`, {
        templateId: selectedTemplate.id
      });
      
      // Emit socket events directly as a fallback
      if (socket) {
        console.log('Emitting template-assigned event with sessionId:', sessionId);
        // Emit template assigned event
        socket.emit('template-assigned', {
          sessionId: sessionId,
          templateId: selectedTemplate.id
        });
      }
      
      setSessionTemplate(selectedTemplate);
      setError(null);
    } catch (err) {
      console.error('Error applying template:', err);
      setError('Failed to apply template to session. Please try again.');
    }
  };


  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
  };

  const renderPreview = () => {
    if (!selectedTemplate && !isEditing && !isCreating) {
      return (
        <div className="text-center p-8 bg-gray-100 rounded-md">
          <p className="text-gray-500">Select a template to preview</p>
        </div>
      );
    }
    
    const template = isEditing || isCreating ? formData : selectedTemplate;
    
    if (!template) return null;
    
    // Replace placeholders in HTML
    let html = template.html
      .replace('{{content}}', previewQuestion)
      .replace('{{#if submitterName}}', showSubmitter ? '' : '<!--')
      .replace('{{submitterName}}', previewSubmitter)
      .replace('{{/if}}', showSubmitter ? '' : '-->');
    
    return (
      <div className="relative">
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Preview</h3>
          <div className="flex space-x-4 mb-4">
            <Input
              label="Sample Question"
              value={previewQuestion}
              onChange={(e) => setPreviewQuestion(e.target.value)}
              className="flex-1"
            />
            <Input
              label="Sample Submitter"
              value={previewSubmitter}
              onChange={(e) => setPreviewSubmitter(e.target.value)}
              className="flex-1"
            />
            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showSubmitter}
                  onChange={(e) => setShowSubmitter(e.target.checked)}
                  className="mr-2"
                />
                Show Submitter
              </label>
            </div>
          </div>
        </div>
        
        <div className="relative border border-gray-300 rounded-md p-4 bg-gray-100 overflow-hidden" style={{ aspectRatio: '16/9' }}>
          
          
          {/* Template preview - Full 16:9 display scaled down */}
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className="absolute"
              style={{ 
                width: '1920px',
                height: '1080px',
                transform: 'scale(0.2)',
                transformOrigin: 'top left',
                left: '0px', 
                top: '0px' 
              }}
            >
              <style>{template.css}</style>
              <div dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleResetTemplates = async () => {
    try {
      await api.post('/api/templates/reset');
      setError(null);
    } catch (err) {
      console.error('Error resetting templates:', err);
      setError('Failed to reset templates. Please try again.');
    }
  };

  // Set up socket event listeners
  useEffect(() => {
    if (!socket) return;

    console.log('Setting up socket event listeners in TemplateEditor');

    // Listen for template events
    socket.on('template-created', (template) => {
      console.log('Received template-created event:', template);
      setTemplates(prev => [...prev, template]);
    });

    socket.on('template-updated', (template) => {
      console.log('Received template-updated event:', template);
      setTemplates(prev => prev.map(t => t.id === template.id ? template : t));
      
      // If this is the currently selected template, update it
      if (selectedTemplate && selectedTemplate.id === template.id) {
        setSelectedTemplate(template);
      }
      
      // If this is the session template, update it
      if (sessionTemplate && sessionTemplate.id === template.id) {
        setSessionTemplate(template);
      }
    });

    socket.on('template-deleted', (data) => {
      console.log('Received template-deleted event:', data);
      setTemplates(prev => prev.filter(t => t.id !== data.id));
      
      // If this is the currently selected template, deselect it
      if (selectedTemplate && selectedTemplate.id === data.id) {
        setSelectedTemplate(null);
      }
    });

    socket.on('templates-reset', () => {
      console.log('Received templates-reset event');
      // Refetch templates
      fetchData();
    });
    

    socket.on('template-assigned', (data) => {
      console.log('Received template-assigned event:', data);
      console.log('Current session ID:', sessionId);
      
      // Compare as strings to avoid type mismatches
      if (String(data.sessionId) === String(sessionId)) {
        console.log('Template assignment is for this session, fetching template');
        // Refetch the session template
        api.get(`/api/sessions/${sessionId}/template`)
          .then(response => {
            setSessionTemplate(response.data);
            // Update selected template if needed
            const template = templates.find(t => t.id === parseInt(data.templateId, 10));
            if (template) {
              setSelectedTemplate(template);
            }
          })
          .catch(err => {
            console.error('Error fetching session template after assignment:', err);
          });
      } else {
        console.log('Template assignment is not for this session');
      }
    });

    // Clean up event listeners when component unmounts or socket changes
    return () => {
      console.log('Cleaning up socket event listeners in TemplateEditor');
      socket.off('template-created');
      socket.off('template-updated');
      socket.off('template-deleted');
      socket.off('templates-reset');
      socket.off('template-assigned');
    };
  }, [socket, selectedTemplate, sessionTemplate, templates, sessionId, fetchData]);

  // Session handling is working correctly now
  
  return (
    <Layout 
      setShowShareModal={setShowShareModal}
      // Pass a sessionOverride prop to Layout
      sessionOverride={!session && localSession ? localSession : undefined}
    >
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-4 md:space-y-0">
          <div className="flex items-center flex-wrap gap-2">
            <Link to={`/session/${sessionId}/manage`} className="mr-2">
              <Button variant="primary" size="sm" className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                <span className="md:inline hidden">Back to Questions</span>
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">Template Editor</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleCreateTemplate} variant="primary" size="sm" className="flex items-center">
              <span className="md:inline hidden">Create New Template</span>
              <span className="md:hidden inline">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              </span>
            </Button>
            <Button onClick={handleResetTemplates} variant="primary" size="sm" className="flex items-center">
              <span className="md:inline hidden">Reset to Defaults</span>
              <span className="md:hidden inline">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </span>
            </Button>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="text-center py-8">
            <p>Loading templates...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <Card>
                <h2 className="text-xl font-semibold mb-4">Templates</h2>
                {templates.length === 0 ? (
                  <p className="text-gray-500">No templates available</p>
                ) : (
                  <div className="space-y-2">
                    {templates.map(template => (
                      <div 
                        key={template.id} 
                        className={`p-3 rounded-md cursor-pointer ${
                          selectedTemplate && selectedTemplate.id === template.id 
                            ? 'bg-blue-100 border border-blue-300' 
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{template.name}</span>
                          {template.is_default === 1 && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Default</span>
                          )}
                        </div>
                        {sessionTemplate && sessionTemplate.id === template.id && (
                          <div className="mt-1 text-xs text-blue-600">Currently applied to this session</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              
              {/* Help Section */}
              <Card className="mt-4">
                <details className="group">
                  <summary className="cursor-pointer text-lg font-semibold text-blue-600 hover:text-blue-800 flex items-center">
                    <span>📖 How to Position Templates</span>
                    <span className="ml-2 text-sm group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="mt-4 space-y-4 text-sm text-gray-700">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Positioning Templates with CSS</h4>
                      <p className="mb-3">Templates are positioned using CSS properties in the template's CSS. Add these properties to your main container class (usually <code className="bg-gray-100 px-1 rounded">.question-container</code>):</p>
                      
                      <div className="bg-gray-50 p-3 rounded-md space-y-2">
                        <div>
                          <strong>Top-left corner:</strong>
                          <pre className="bg-gray-800 text-green-400 p-2 rounded mt-1 text-xs overflow-x-auto">
{`.question-container {
    position: absolute;
    left: 0%;
    top: 0%;
}`}
                          </pre>
                        </div>
                        
                        <div>
                          <strong>Center of screen:</strong>
                          <pre className="bg-gray-800 text-green-400 p-2 rounded mt-1 text-xs overflow-x-auto">
{`.question-container {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
}`}
                          </pre>
                        </div>
                        
                        <div>
                          <strong>Bottom-right corner:</strong>
                          <pre className="bg-gray-800 text-green-400 p-2 rounded mt-1 text-xs overflow-x-auto">
{`.question-container {
    position: absolute;
    left: 100%;
    top: 100%;
    transform: translate(-100%, -100%);
}`}
                          </pre>
                        </div>
                        
                        <div>
                          <strong>Custom position (e.g., 20% from left, 30% from top):</strong>
                          <pre className="bg-gray-800 text-green-400 p-2 rounded mt-1 text-xs overflow-x-auto">
{`.question-container {
    position: absolute;
    left: 20%;
    top: 30%;
}`}
                          </pre>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Full Screen Template</h4>
                      <p className="mb-2">The "Full Screen" template automatically fills the entire display area. It uses:</p>
                      <pre className="bg-gray-800 text-green-400 p-2 rounded text-xs overflow-x-auto">
{`.fullscreen-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}`}
                      </pre>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Tips</h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-600">
                        <li>Use percentages (0% to 100%) for responsive positioning</li>
                        <li>Use <code className="bg-gray-100 px-1 rounded">transform: translate(-50%, -50%)</code> to center content</li>
                        <li>Always use <code className="bg-gray-100 px-1 rounded">position: absolute</code> for positioning</li>
                        <li>Test your positioning in the preview before applying to the session</li>
                      </ul>
                    </div>
                  </div>
                </details>
              </Card>

              {/* QR Template Settings Card */}
              <Card className="mt-4">
                <QRTemplateSettings sessionId={sessionId} socket={socket} />
              </Card>

              {selectedTemplate && !isEditing && !isCreating && (
                <Card className="mt-4">
                  <h2 className="text-xl font-semibold mb-4">Template Actions</h2>
                  <div className="space-y-2">
                    <Button onClick={handleEditTemplate} variant="primary" className="w-full">Edit Template</Button>
                    <Button 
                      onClick={handleApplyTemplate} 
                      className="w-full"
                      variant="primary"
                      disabled={sessionTemplate && sessionTemplate.id === selectedTemplate.id}
                    >
                      Apply to Session
                    </Button>
                  </div>
                </Card>
              )}
            </div>
            
            <div className="lg:col-span-2">
              {isEditing || isCreating ? (
                <Card>
                  <h2 className="text-xl font-semibold mb-4">
                    {isEditing ? `Edit Template: ${formData.name}` : 'Create New Template'}
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Template Name
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          HTML Template
                        </label>
                        <textarea
                          name="html"
                          value={formData.html}
                          onChange={handleChange}
                          rows="10"
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        ></textarea>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CSS Styles
                        </label>
                        <textarea
                          name="css"
                          value={formData.css}
                          onChange={handleChange}
                          rows="10"
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        ></textarea>
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-2">
                      <Button onClick={handleCancel} variant="primary">Cancel</Button>
                      <Button onClick={handleSaveTemplate} variant="primary">Save Template</Button>
                    </div>
                  </div>
                </Card>
              ) : (
                <Card>
                  {renderPreview()}
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Share Links Modal */}
      <ShareLinksModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        sessionId={sessionId}
      />
    </Layout>
  );
};

export default TemplateEditor; 